import { useTranslation } from 'next-i18next/pages';
import React from 'react';

import { useMediaQuery } from '@/hooks/shared/useMediaQuery';

import {
  SOLUTION_RESOURCES,
  SOLUTION_RESOURCE_PRODUCT_NAMES,
  type SolutionResource,
  type SolutionResourceFormat,
} from '@/data/shared/solutionResources';

import Area from '@/components/apps/layouts/shared/Area';
import { IconArrowUpRightFromSquareRound } from '@/components/apps/ui/icon';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';
import { AniSlideUp } from '@/components/shared/animations/basic';

import theme from '@/styles/theme';

import ResourceSectionItem from './ResourceSectionItem';

/**
 * 원본은 hover 시 @media (hover: hover) 안에서 `> div` 색과 ResourceIcon 배경을 바꿨다.
 * Tailwind 의 hover/group-hover 변형은 이미 @media (hover: hover) 로 감싸지므로
 * 링크에 group 을 걸고 자식에서 group-hover 로 받는다.
 * 아이콘 배경색은 applyOpacityToColor(coreGrayBlack #161617, 0.06) 의 결과값을 그대로 적었다.
 */
const RESOURCE_ITEM_CLASS =
  'group grid grid-cols-[auto_40px] items-center gap-[8px] rounded-[12px] bg-apps-coreGrayWhite px-[20px] py-[16px] text-apps-brandGray50 shadow-[0px_0px_20px_0px_rgba(0,0,0,0.04)] lg:items-end lg:rounded-[20px] lg:px-[clamp(16px,2.571vw,36px)] lg:py-[clamp(16px,2.571vw,36px)]';

const RESOURCE_ICON_CLASS =
  'inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-[8px] transition-[background-color] duration-200 ease-[ease] group-hover:bg-[rgba(22,22,23,0.06)]';

/*
 * This section has printed the product name under the two overviews only,
 * since it launched. The resource page prints it on every card instead, so the
 * rule is a per-screen layout decision and stays with the screen that makes
 * it rather than in the shared resource list.
 */
const PRODUCT_NAME_FORMATS: ReadonlyArray<SolutionResourceFormat> = [
  'overview',
];

function productNameOf(resource: SolutionResource) {
  return PRODUCT_NAME_FORMATS.includes(resource.format)
    ? SOLUTION_RESOURCE_PRODUCT_NAMES[resource.product]
    : undefined;
}

export default function ResourceSection() {
  const { t } = useTranslation('common');

  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme`screens.lg`})`,
  });

  return (
    <section className="bg-apps-coreGray07 py-[100px] lg:py-[clamp(100px,14.286vw,200px)]">
      <Area>
        <SectionTitle
          title={t('솔루션 소개 자료')}
          description={t(
            'Listen AI에 대한 더 다양한 정보를 자료로 살펴보세요.',
          )}
        />
        <AniSlideUp>
          <div className="mt-[40px] grid gap-[14px] lg:mt-[clamp(40px,5vw,60px)] lg:grid-cols-2 lg:gap-[16px]">
            {SOLUTION_RESOURCES.map((resource) => (
              <ResourceSectionItem
                className={RESOURCE_ITEM_CLASS}
                key={resource.id}
                productName={productNameOf(resource)}
                resource={resource}
              >
                <span className={RESOURCE_ICON_CLASS} aria-hidden="true">
                  <IconArrowUpRightFromSquareRound
                    size={isDesktop ? 32 : 24}
                    color={theme`colors.apps.coreGrayBlack`}
                  />
                </span>
              </ResourceSectionItem>
            ))}
          </div>
        </AniSlideUp>
      </Area>
    </section>
  );
}
